import {formatTimestampDate} from '../dates';
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { alphaApi as api } from './alpha-request';
import { ErrorState, Loading } from '../ui/State';
export function PortalNoticesAlpha() {
  const [items, setItems] = useState<any[]>(),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    void api<{ items: any[] }>('/api/portal/notices')
      .then((r) => setItems(r.items))
      .catch(setError);
  }, []);
  return (
    <>
      <h1>Building notices</h1>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!items && !error ? (
        <Loading />
      ) : !items?.length ? (
        <p>No notices are shared with you.</p>
      ) : (
        items.map((i: any) => (
          <article className="panel" key={i.id + i.property}>
            <h2>{i.title}</h2>
            <p>
              {i.property} · {formatTimestampDate(i.published_at)}
            </p>
            <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{i.body}</p>
          </article>
        ))
      )}
    </>
  );
}
export function BuildingNoticesAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building'),
    [data, setData] = useState<any>(),
    [preview, setPreview] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  const load = () => api(`/api/alpha/building-notices?building=${building}`).then(setData);
  useEffect(() => {
    if (building) void load().catch(setError);
  }, [building]);
  async function run(url: string, body: object) {
    setBusy(true);
    try {
      const r = await api<any>(url, {
        method: 'POST',
        body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
      });
      await load();
      setError(undefined);
      setNotice('Saved.');
      return r;
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      r = await run('/api/alpha/building-notices', {
        building,
        title: f.get('title'),
        body: f.get('body'),
        premisesIds: f.getAll('premises'),
      });
    if (r) await inspect(r.id);
  }
  async function inspect(id: string) {
    try {
      setPreview(await api(`/api/alpha/building-notices/${id}/preview`));
      setError(undefined);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <>
      <h1>Building notices</h1>
      <p>
        Prepare a notice, review its exact recipients, then publish. Email delivery remains awaiting
        approval or suppressed; this alpha does not send mail.
      </p>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {!building ? (
        <p>Open this page in a building.</p>
      ) : !data && !error ? (
        <Loading />
      ) : !data ? null : (
        <>
          <section className="panel">
            <h2>Prepare a notice</h2>
            <form className="form" onSubmit={create}>
              <label>
                Title
                <input name="title" required minLength={3} maxLength={200} />
              </label>
              <label>
                Notice
                <textarea name="body" required minLength={10} maxLength={10000} />
              </label>
              <fieldset>
                <legend>Properties</legend>
                {data.premises.map((p: any) => (
                  <label key={p.id}>
                    <input type="checkbox" name="premises" value={p.id} />
                    {p.display_name}
                  </label>
                ))}
              </fieldset>
              <button disabled={busy}>Save draft and review</button>
            </form>
          </section>
          <h2>Saved notices</h2>
          {data.items.map((i: any) => (
            <p key={i.id}>
              <button onClick={() => void inspect(i.id)}>{i.title}</button> · {i.status}
            </p>
          ))}
        </>
      )}
      {preview && (
        <section className="panel">
          <h2>Review: {preview.notice.title}</h2>
          <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{preview.notice.body}</p>
          <h3>Recipients and delivery</h3>
          {preview.recipients.map((p: any) => (
            <p key={p.person_id + p.premises_id}>
              {p.display_name} · {p.premises_name} · {p.destination ?? p.reason} · {p.status}
            </p>
          ))}
          {preview.notice.status === 'Draft' ? (
            <button
              disabled={busy}
              onClick={async () => {
                const r = await run(`/api/alpha/building-notices/${preview.notice.id}/publish`, {
                  expectedVersion: preview.notice.version,
                  reviewHash: preview.reviewHash,
                });
                if (r) await inspect(r.id);
              }}
            >
              Publish reviewed notice
            </button>
          ) : preview.notice.status === 'Published' ? (
            <button
              disabled={busy}
              onClick={async () => {
                await run(`/api/alpha/building-notices/${preview.notice.id}/withdraw`, {});
                await inspect(preview.notice.id);
              }}
            >
              Withdraw notice
            </button>
          ) : null}
          <h3>Delivery history</h3>
          <p>Portal delivery means available to the recipient; it does not mean read.</p>
          {preview.deliveries.map((d: any) => (
            <p key={d.id}>
              {d.channel} · {d.status} · {d.last_error ?? ''}
            </p>
          ))}
        </section>
      )}
    </>
  );
}
